import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { fetchJson } from '../Helpers/Helpers';
import config from '../../config';

import DatePickerUI from '../DatePicker/DatePicker';
import { DetailsItem } from '../Details/Details';
import Dropdown from '../Dropdown/Dropdown';
import { FormDialog } from '../FormDialog/FormDialog';
import { useHasAnonymousError } from '../FormView/FormView';
import { TextField, Autocomplete } from '@mui/material';

function NewInspectionModal(props, {errorElements = []}) {
	const { t } = useSelector(redux => redux.store);
	const [fields, setFields] = useState({});
	const [message, setMessage] = useState(t.new_inspection_loading);
	const history = useHistory();

	// not sure?
	const hasAnonymousError = useHasAnonymousError(errorElements);

	/**
	 * On mount, fetch form fields and options from the BFF
	 *
	 * Note: I use a `mounted` variable, because App.js may trigger a rerender
	 * of all its childs after translations are loaded. Because of our async
	 * call to 'create-inspection', setFields and setMessage will try to
	 * update state while this component is unmounted. I know this is an anti
	 * pattern but I'm out of time and moving the fetch call elsewhere is a big
	 * refactor
	 */
	useEffect(() => {
		let mounted = true;
		if (navigator.onLine) {
			fetchJson('create-inspection').then(json => {
				if (mounted) {
					setFields(json.data);
					setMessage('');
				}
			}).catch(error => {
				// @TODO add logging
				console.error(error);
				setMessage(t.new_inspection_loading_failed);
			});
		} else {
			setMessage(t.new_inspection_online_only);
		}

		return () => {
			mounted = false;
		};
	}, [t]);

	/**
	 * On form submit
	 */
	function submitForm() {
		if (navigator.onLine) {
			return fetch(`${config.api.url}/create-inspection`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify(getPostdata()),
			})
				.then(response => {
					if (response.ok) {
						props.closeHandler(); // mag dit?
						return response.json();
					} else {
						throw Error(response);
					}
				})
				.catch(console.error);
		} else {
			setMessage(t.new_inspection_online_only);
			return Promise.resolve();
		}
	}

	/**
	 * Handles primary button, in our case submits the form
	 * and redirects to the new inspection immediately
	 */
	async function handlePrimary() {
		const { id } = await submitForm();
		history.push(`/inspection/${id}/(nieuw)/${t.menu_installation}`);
	}

	/**
	 * Handles secondary button, in our case submits the form.
	 * Refreshing the overview happens in the close handler that
	 * is called after a successful submit.
	 */
	async function handleSecondary() {
		await submitForm();
	}

	/**
	 * Transforms fields object into a key value pair object that the remote
	 * API can consume
	 *
	 * Structure:
	 * { field_name: value, field_name_2: value_2, ... }
	 *
	 * @return {Object}
	 */
	function getPostdata() {
		return Object.keys(fields).reduce((collection, fieldName) => {
			return { ...collection, ...{ [fieldName]: fields[fieldName].value } };
		}, {});
	}

	/**
	 * On change of every form field, the field name and value are passed
	 * and the state object for all fields is updated
	 *
	 * @param  {String} fieldName
	 * @param  {Any} value
	 */
	function onChange(fieldName, value) {
		let changedFields = { ...fields };
		changedFields[fieldName].value = value;
		setFields(changedFields);
	}

	return (
		<FormDialog
			title={t.new_inspection}
			isOpen={ props.open }
			closeHandler={ props.closeHandler }
			canBeCanceled={true}
			primaryAction={{
				label: t.new_inspection_execute,
				handler: handlePrimary,
			}}
			secondaryAction={{
				label: t.action_addnew_inspection_add,
				handler: handleSecondary,
			}}
			isDisabled={ (() => !navigator.onLine)() }
		>
			{ fields &&
				Object.values(fields).map((field, index) => (
					<DetailsItem
						key={index}
						label={field.label}
						type={field.type}
						required={field.required}
						readOnly={field.readOnly}
						error={ hasAnonymousError(field.name) }
					>
						<BasicFormField
							onChange={ onChange }
							field={ field }
							errorElements={ errorElements }
						/>
					</DetailsItem>
				))
			}
			{ message &&
				<p>{ message }</p>
			}
		</FormDialog>
	);
}

function BasicFormField(props) {
	const { t } = useSelector(redux => redux.store);
	const field = props.field;
	const errorElements = props.errorElements;

	const hasAnonymousError = useHasAnonymousError(errorElements);

	/**
	 * Filter options for autocomplete; if less than 3 characters, the dropdown
	 * will be empty. Otherwise, simple filter will be applied (text is matched
	 * as-is, so "blue pony" will match "My blue ponytrailer" but not "My blue
	 * shetland pony")
	 *
	 * @param  {Array} options
	 * @param  {Object} state
	 * @return {Array}
	 */
	function filterOptions(options, state) {
		if (state.inputValue.length > 2) {
			return options.filter((option) => {
				return String(option.title).toLowerCase().includes(state.inputValue.toLowerCase());
			});
		}
		return [];
	}

	switch(field.type) {
	case 'autocomplete':
		return (
			<Autocomplete
				required={ field.required }
				id={ field.name }
				options={ field.options }
				filterOptions={ filterOptions }
				getOptionLabel={ (option) => option.title }
				name={ field.name }
				noOptionsText={ t.new_inspection_no_options || 't.new_inspection_no_options' }
				onChange={ (event, changedField) => {
					props.onChange(field.name, changedField && changedField.value);
				}}
				renderInput={(params) => (
					<TextField variant="standard" {...params} />
				)}
			/>
		);
	case 'select':
		return <Dropdown
			data-notrequired={!field.required || null}
			fullWidth
			value={field.value}
			name={field.name}
			onChange={ (event) => {
				props.onChange(field.name, event.target.value);
			}}
			options={field.options}
			readOnly={field.readOnly}
			error={ hasAnonymousError(field.name) }
		/>;
	case 'number':
		return (
			<TextField
				variant="standard"
				inputProps={{'data-notrequired': !field.required || null}}
				fullWidth
				defaultValue={field.value}
				type="number"
				name={field.name}
				onChange={ (event) => {
					props.onChange(field.name, event.target.value);
				}}
				readOnly={field.readOnly}
				error={ hasAnonymousError(field.name) } />
		);
	case 'date':
		return <DatePickerUI
			t={t}
			data-notrequired={!field.required || null}
			label={t.select_date}
			name={field.name}
			value={field.value}
			readOnly={field.readOnly}
			onChange={ (self, date) => {
				props.onChange(field.name, date.format('DD-MM-YYYY')); // date is moment object
			}}
			error={ hasAnonymousError(field.name) }
		/>;
	default:
	case 'string':
		return (
			<TextField
				variant="standard"
				inputProps={{'data-notrequired': !field.required || null}}
				fullWidth
				defaultValue={field.value}
				name={field.name}
				onChange={ (event) => {
					props.onChange(field.name, event.target.value);
				}}
				readOnly={field.readOnly}
				error={ hasAnonymousError(field.name) } />
		);
	}

}

export default NewInspectionModal;

